/**
 * Content Security Policy (CSP) utility functions
 * Handles nonce generation and CSP policy management for different environments
 */

// Environment detection using your existing environment variables
export const env = {
  APP_ENV: (import.meta.env['VITE_APP_ENV'] || 'development') as 'development' | 'staging' | 'production',
  API_URL: import.meta.env['VITE_API_URL'] || 'http://localhost:4000',
  MODE: import.meta.env.MODE, // Vite's build mode
  
  // Derived environment checks
  get isDevelopment() {
    return this.APP_ENV === 'development';
  },
  
  get isStaging() {
    return this.APP_ENV === 'staging';
  },
  
  get isProduction() {
    return this.APP_ENV === 'production';
  },
  
  get isViteDev() {
    return this.MODE === 'development';
  },
  
  // Runtime hostname detection for localhost
  get isLocalhost() {
    if (typeof window === 'undefined') {
      return false;
    }
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
  }
};

/**
 * Get the CSP nonce from the meta tag injected during build
 * Returns null in development mode when using unsafe-inline
 */
export function getCSPNonce(): string | null {
  const metaTag = document.querySelector('meta[name="csp-nonce"]');
  const nonce = metaTag?.getAttribute('content');
  
  // Don't use nonce in development mode or Vite dev mode
  if (env.isDevelopment || env.isViteDev || nonce === 'dev-mode') {
    return null;
  }
  
  return nonce || null;
}

/**
 * Create a script element with CSP nonce if available
 * This ensures dynamically created scripts comply with CSP
 */
export function createScriptWithNonce(src?: string): HTMLScriptElement {
  const script = document.createElement('script');
  const nonce = getCSPNonce();
  
  if (nonce) {
    script.setAttribute('nonce', nonce);
  }
  
  if (src) {
    script.src = src;
  }
  
  return script;
}

/**
 * Create a style element with CSP nonce if available
 * This ensures dynamically created styles comply with CSP
 */
export function createStyleWithNonce(): HTMLStyleElement {
  const style = document.createElement('style');
  const nonce = getCSPNonce();
  
  if (nonce) {
    style.setAttribute('nonce', nonce);
  }
  
  return style;
}

/**
 * Check if a URL is allowed by CSP connect-src directive
 * Useful for validating API endpoints before making requests
 */
export function isAllowedConnectSource(url: string): boolean {
  try {
    const urlObj = new URL(url);
    
    // Always allow same-origin requests
    if (urlObj.origin === window.location.origin) {
      return true;
    }
    
    // Allow localhost in development
    if (env.isDevelopment && urlObj.hostname === 'localhost') {
      return true;
    }
    
    // Allow Heroku backends in staging and production
    if ((env.isStaging || env.isProduction) && 
        (urlObj.hostname.endsWith('.herokuapp.com') || urlObj.hostname.endsWith('.herokucdn.com'))) {
      return true;
    }
    
    return false;
  } catch {
    return false;
  }
}

/**
 * Get CSP policy based on current environment
 * Returns the appropriate CSP directive set for the current environment
 * Uses runtime hostname detection to match Vercel's localhost policy
 */
export function getCurrentCSPPolicy(): string {
  // Check for localhost at runtime to match Vercel's localhost policy
  if (env.isLocalhost || env.isViteDev) {
    return CSP_DIRECTIVES.development;
  } else if (env.isStaging) {
    return CSP_DIRECTIVES.staging;
  } else {
    return CSP_DIRECTIVES.production;
  }
}

/**
 * CSP directive values for different environments
 * These match the policies defined in vercel.json
 */
export const CSP_DIRECTIVES = {
  development: "default-src 'self'; script-src 'self' 'unsafe-eval' 'unsafe-inline' https://js.hs-scripts.com https://js.hsadspixel.net https://js.hubspot.com https://js.hs-banner.com https://js.hs-analytics.net https://connect.facebook.net; style-src 'self' 'unsafe-inline'; img-src 'self' data: blob:; font-src 'self'; connect-src 'self' http://localhost:* https://localhost:* ws://localhost:* wss://localhost:* https://api.hubapi.com https://api.hubspot.com https://*.hubspot.com https://static.hsappstatic.net https://www.facebook.com; frame-src https://*.hs-sites.com https://*.hubspot.com; object-src 'none'; base-uri 'self'; form-action 'self';",
  
  staging: "default-src 'self'; script-src 'self' 'unsafe-inline' https://cdn.jsdelivr.net https://js.hs-scripts.com https://js.hsadspixel.net https://js.hubspot.com https://js.hs-banner.com https://js.hs-analytics.net https://connect.facebook.net; style-src 'self' 'unsafe-inline'; img-src 'self' data: blob: https:; font-src 'self'; connect-src 'self' https://*.herokuapp.com https://*.herokucdn.com https://*.b-cdn.net https://api.hubapi.com https://api.hubspot.com https://*.hubspot.com https://static.hsappstatic.net https://www.facebook.com; media-src 'self' https://*.b-cdn.net blob: data:; worker-src 'self' blob:; frame-src https://*.hs-sites.com https://*.hubspot.com; object-src 'none'; base-uri 'self'; form-action 'self'; upgrade-insecure-requests;",
  
  production: "default-src 'self'; script-src 'self' 'unsafe-inline' https://cdn.jsdelivr.net https://js.hs-scripts.com https://js.hsadspixel.net https://js.hubspot.com https://js.hs-banner.com https://js.hs-analytics.net https://connect.facebook.net; style-src 'self' 'unsafe-inline'; img-src 'self' data: blob: https:; font-src 'self'; connect-src 'self' https://*.herokuapp.com https://*.herokucdn.com https://*.b-cdn.net https://api.hubapi.com https://api.hubspot.com https://*.hubspot.com https://static.hsappstatic.net https://www.facebook.com; media-src 'self' https://*.b-cdn.net blob: data:; worker-src 'self' blob:; frame-src https://*.hs-sites.com https://*.hubspot.com; object-src 'none'; base-uri 'self'; form-action 'self'; upgrade-insecure-requests; block-all-mixed-content;"
} as const;

/**
 * Log CSP information for debugging
 * Only logs in development or when debug logging is enabled
 */
export function logCSPInfo(): void {
  if (!env.isDevelopment && !globalThis.localStorage?.getItem('debug-csp')) {
    return;
  }
  
  const nonce = getCSPNonce();
  const currentPolicy = document.querySelector('meta[http-equiv="Content-Security-Policy"]')?.getAttribute('content');
  
  console.group('🔒 Content Security Policy Info');
  console.log('CSP Nonce:', nonce || 'Not available');
  console.log('Current Policy:', currentPolicy || 'Not set via meta tag');
  console.log('Connect Source Check:', isAllowedConnectSource(env.API_URL));
  console.log('Expected CSP Policy:', getCurrentCSPPolicy());
  console.groupEnd();
}

// Auto-log CSP info in development
if (env.isDevelopment || env.isViteDev) {
  logCSPInfo();
} 